import React from 'react';

interface BlogPost {
  id: number;
  category: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  author: string;
  authorAvatar: string;
  date: string;
  url: string;
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    category: 'Furniture Sales',
    title: 'How 3D Configurators Reduce Product Returns and Boost Sales',
    excerpt: 'In today’s competitive market, customer satisfaction is the backbone of successful businesses. But one of the biggest challenges retailers and manufacturers face is product returns',
    imageUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/Blog/Wordpress-Blog-Title-2-783x480.webp',
    author: 'Kartik Katahre',
    authorAvatar: 'https://i.pravatar.cc/150?img=4',
    date: 'Aug 25, 2025',
    url: 'https://omniviewlabs.com/how-3d-configurators-reduce-product-returns-and-boost-sales/',
  },
  {
    id: 2,
    category: 'Product Visualization',
    title: 'Smarter Product Visualization: How AI Background Cleanup Enhances Your 3D Customization Experience',
    excerpt: 'When customers customize products online, they want to see exactly how those products will look in their own space.',
    imageUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/Blog/Wordpress-Blog-Title-1-783x480.webp',
    author: 'Kartik Katahre',
    authorAvatar: 'https://i.pravatar.cc/150?img=5',
    date: 'Sep 28, 2023',
    url: 'https://omniviewlabs.com/smarter-product-visualization-how-ai-background-cleanup-enhances-your-3d-customization-experience/',
  },
  {
    id: 3,
    category: 'Case Study',
    title: 'From One 3D Model to Infinite Possibilities: Unlocking Configurations with a 3D Product Configurator',
    excerpt: 'Imagine having a single 3D model of a product—like a sofa, wardrobe, or chair—and being able to show customers every possible variation of it: different colors, materials, sizes, and layouts.',
    imageUrl: 'https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/Blog/Wordpress-Blog-Title-783x480.webp',
    author: 'Kartik Katahre',
    authorAvatar: 'https://i.pravatar.cc/150?img=1',
    date: 'Sep 15, 2023',
    url: 'https://omniviewlabs.com/from-one-3d-model-to-infinite-possibilities-unlocking-configurations-with-a-3d-product-configurator/',
  },
];

const BlogPostCard: React.FC<{ post: BlogPost }> = ({ post }) => {
  return (
    <a 
      href={post.url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="group block rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out transform hover:-translate-y-2 bg-white border border-gray-200"
    >
      <article className="flex flex-col h-full">
        <div className="relative">
          <img src={post.imageUrl} alt={post.title} className="w-full h-56 object-cover" />
          <div className="absolute top-4 left-4">
            <span className="inline-block bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">{post.category}</span>
          </div>
        </div>
        <div className="p-6 flex flex-col flex-grow">
          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
            {post.title}
          </h3>
          <p className="text-gray-600 leading-relaxed mb-6 flex-grow">{post.excerpt}</p>
          <div className="mt-auto flex items-center pt-4 border-t border-gray-100">
            <div>
              <p className="font-semibold text-gray-800">{post.author}</p>
              <p className="text-sm text-gray-500">{post.date}</p>
            </div>
          </div>
        </div>
      </article>
    </a>
  );
};


const Blog: React.FC = () => {
  return (
    <section id="blog" className="py-10 md:py-16 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 data-animation="fade-in-up" className="text-3xl md:text-4xl font-bold text-gray-900">From Our Blog</h2>
          <p data-animation="fade-in-up" style={{ transitionDelay: '200ms' }} className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
            Stay updated with the latest trends in 3D visualization, AR, and the future of retail.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <div key={post.id} data-animation="zoom-in" style={{ transitionDelay: `${200 * index + 400}ms` }}>
              <BlogPostCard post={post} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
